import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiCalendar, FiUser, FiArrowRight } from 'react-icons/fi';
import api from '../utils/api';
import './Blog.css';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await api.get('/blog');
      // API returns { success: true, data: [...] }
      const posts = response.data?.data || response.data || [];
      setPosts(Array.isArray(posts) ? posts : []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="blog page-transition">
      <Helmet>
        <title>Blog - Couriex</title>
        <meta name="description" content="Stay updated with the latest news, tips, and insights from Couriex" />
      </Helmet>
      {/* Hero Section */}
      <section className="blog-hero">
        <div className="container">
          <motion.div
            className="blog-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="blog-hero-title">Our Blog</h1>
            <p className="blog-hero-subtitle">
              Stay updated with the latest news, tips, and insights from Couriex
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="blog-posts">
        <div className="container">
          {isLoading ? (
            <div className="blog-loading">Loading posts...</div>
          ) : posts.length === 0 ? (
            <div className="blog-empty">
              <p>No blog posts available yet. Check back soon!</p>
            </div>
          ) : (
            <motion.div
              className="blog-grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              {posts.map((post, index) => (
                <motion.article
                  key={post.id}
                  className="blog-card"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  {post.image && (
                    <div className="blog-card-image">
                      <img 
                        src={post.image.startsWith('http') ? post.image : `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${post.image}`} 
                        alt={post.title} 
                      />
                    </div>
                  )}
                  <div className="blog-card-content">
                    <div className="blog-card-meta">
                      <span className="blog-meta-item">
                        <FiUser /> {post.author}
                      </span>
                      <span className="blog-meta-item">
                        <FiCalendar /> {formatDate(post.published_at)}
                      </span>
                    </div>
                    <h2 className="blog-card-title">{post.title}</h2>
                    <p className="blog-card-excerpt">{post.excerpt}</p>
                    <Link to={`/blog/${post.slug || post.id}`} className="blog-card-link">
                      Read More <FiArrowRight />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;

